import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import identityApi from '../clients/IdentityClient';
import LogoLink from './LogoLink';
import { currentTenant } from '../tenant/tenants';

const VerifyEmail: React.FC = () => {
  const { guid } = useParams<{ guid: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRedirectTimer = () => {
    if (redirectTimer.current) {
      clearTimeout(redirectTimer.current);
      redirectTimer.current = null;
    }
  };

  useEffect(() => {
    const verifyEmail = async () => {
      setStatus('loading');
      setMessage('');

      if (!guid) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        const response = await identityApi.verifyEmail(guid);
        if (response.ok) {
          setStatus('success');
          setMessage('Your email has been verified');

          clearRedirectTimer();
          redirectTimer.current = setTimeout(() => {
            navigate(`/newpassword/${guid}?mode=create`);
          }, 3000);
        } else {
          const errorMessage = await response.text();
          setStatus('error');

          setMessage(`Email verification failed (${errorMessage}).
           Please contact our support at ${currentTenant.brand.supportEmail}`);
        }
      } catch (error) {
        setStatus('error');
        setMessage(`Email verification failed. Contact our support at ${currentTenant.brand.supportEmail}`);
      }
    };

    verifyEmail();
    return () => {
      clearRedirectTimer();
    };
  }, [guid, navigate]);

  const getAlertVariant = () => {
    if (status === 'success') return 'success';
    if (status === 'error') return 'danger';
    return 'info';
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="card">
          <div className="text-center mb-4">
            <LogoLink className="navbar-brand" imgClassName="me-2" />
            <h3 className="text-accent m-2 heading">Email Verification</h3>
          </div>

          <div className={`alert alert-${getAlertVariant()} text-center`} role="status">
            {status === 'loading' ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Verifying your email...
              </>
            ) : (
              <span>{message}</span>
            )}
          </div>

          {status === 'success' && (
            <div className="text-center mt-3">
              <small className="text-paragraphs d-block mb-2">
                Redirecting to the password setup page in 3 seconds.
              </small>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center mt-3">
              <button type="button" className="btn btn-primary" onClick={() => navigate('/login')}>
                Return to login
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;